import type { Branding } from './types';

/**
 * The mark a candidate sees above their exam, if there is one.
 *
 * A centre's logo where it has uploaded one, its name where it has not — that
 * is the rule the login screen follows and the one the exam bar was written
 * for. It had no answer for the platform's own workspace, which is not a
 * centre at all: the name it carries is the name the people running the
 * platform gave a shelf of papers, "Platform library", and a candidate
 * enrolled directly under that account sat their exam under it.
 *
 * So the workspace's name is not shown. A logo still is — uploading one is a
 * deliberate choice about what candidates should see, and this is only about
 * an internal label being mistaken for a brand.
 */
export type CandidateMark = { logoUrl: string; alt: string } | { name: string } | null;

export function candidateMark(org: Branding | null | undefined): CandidateMark {
  if (!org) return null;
  if (org.logoUrl) return { logoUrl: org.logoUrl, alt: org.name ?? '' };
  if (org.isPlatform) return null;
  return org.name ? { name: org.name } : null;
}

/**
 * Whether a logo was drawn on a solid background, judged by its four corners.
 *
 * The sign-in page draws a centre's logo faintly under the form by using its
 * shape as a mask, and a mask only works where the image is transparent
 * around the mark. A logo saved as a JPG — the mark on a white rectangle —
 * would come out as a faint grey box. Four opaque corners is the sign of
 * that; a mark on a transparent ground has at least one clear corner.
 *
 * No corners (the image could not be read) is not a solid background: the
 * page draws the emblem rather than hide a logo it merely failed to inspect.
 */
export function drawnOnBackground(cornerAlphas: number[]): boolean {
  return cornerAlphas.length === 4 && cornerAlphas.every((alpha) => alpha >= 250);
}
